import Editor, { useMonaco } from '@monaco-editor/react';
import { editor } from 'monaco-editor';
import { useRef, useState } from 'react';
import { CWTSchema } from '../../../types/custom-worksheet-v2';
import { useDispatch } from 'react-redux';
import { editCalculation } from '../store/slice';
import { FieldReferenceMap } from './types';
import { calcToYaml, initMonacoWebworker, yamlToCalc } from './utils';
import { useConfigureSandbox } from './use-configure-sandbox';
import { ButtonV2 } from '@cimplico/components';
import { CheckIcon } from '@heroicons/react/20/solid';
import { isCWTSchemaTable } from '../store/utils';

type CalculationEditorProps = {
    schema: CWTSchema;
    references: FieldReferenceMap[];
    selectedReference: FieldReferenceMap;
};

initMonacoWebworker();

function CalculationEditor({ schema, references, selectedReference }: CalculationEditorProps): JSX.Element {
    const monaco = useMonaco();
    const saveRef = useRef<HTMLParagraphElement>(null);
    const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
    const [markers, setMarkers] = useState<editor.IMarker[]>([]);

    const dispatch = useDispatch();

    useConfigureSandbox({
        monaco,
        availableReferences: references,
        currentField: selectedReference.field,
        schema,
    });

    function handleSave(): void {
        const value = editorRef.current?.getValue();
        const markers = monaco?.editor.getModelMarkers({ owner: 'yaml' });

        if (markers && markers.length > 0) {
            return;
        }

        dispatch(
            editCalculation({
                id: selectedReference.field.id,
                value: yamlToCalc(selectedReference.field.id, schema, value || '', references),
            })
        );
        const current = saveRef.current;

        if (current) {
            current.classList.remove('invisible');
            setTimeout(() => {
                current.classList.add('invisible');
            }, 1000);
        }
    }

    return (
        <div className="relative w-[650px]">
            <div className="h-[400px] flex flex-col items-end gap-2">
                <div className="border rounded-lg h-full w-full">
                    <Editor
                        defaultLanguage="yaml"
                        onMount={(editor): void => {
                            editorRef.current = editor;
                        }}
                        value={calcToYaml(
                            isCWTSchemaTable(selectedReference.field)
                                ? selectedReference.field?.generatedFromFunction
                                : selectedReference?.field?.calculation
                        )}
                        path={`$calc.yaml`}
                        options={{ minimap: { enabled: false } }}
                        className="rounded-lg"
                        onValidate={setMarkers}
                    />
                </div>
                <div className="flex items-center gap-2">
                    <p className="text-sm text-gray-700 flex items-center invisible" ref={saveRef}>
                        Saved <CheckIcon className="h-4 w-4 text-green-600 ml-1" />
                    </p>
                    <ButtonV2
                        label="Save Changes"
                        size="xs"
                        onClick={handleSave}
                        isDisabled={markers.length > 0}
                        disabledHint="Please address all warnings."
                    />
                </div>
            </div>
        </div>
    );
}

export default CalculationEditor;
